//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import {
  FulfillmentDetails,
  SamplingCampaignRequestCountry,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import {
  ContentCard,
  DetailLabel,
  DetailRow,
  MultiLineValue,
} from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/quote-section/SamplingCampaignRequestQuoteSharedStyles';
import { SamplingCampaignRequestClientFulfillmentSummary } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/quote-section/summary/SamplingCampaignRequestClientFulfillmentSummary';
import { SamplingCampaignRequestDeliverySummary } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/quote-section/summary/SamplingCampaignRequestDeliverySummary';
import { renderPackageWeight } from '@campaigns/components/campaign-requests/sampling-campaigns/shared/sections/packaging-section/PackagingSection';
import {
  BOX_OPTIONS,
  FULFILLMENT_TYPE_SHIPPED,
  WHO_SHIPS_PRODUCT_BAZAARVOICE,
  WHO_SHIPS_PRODUCT_MY_COMPANY,
  getPackageInsertOptions,
} from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import Icon from '@components/atoms/icon/Icon';
import {
  BodyBoldText,
  BodyText,
  CaptionText,
  TitleText,
} from '@components/atoms/text/Text';
import VerticalDivider from '@components/atoms/vertical-divider/VerticalDivider';
import { DEFAULT_EMPTY_VALUE } from '@constants/DefaultEmptyValues';
import { isDefined } from '@utils/filterPredicates';

export interface QuoteSummaryBreakdownContent {
  units?: number;
  writeReview?: boolean;
  postOnSocial?: boolean;
  postSurvey?: boolean;
}

export interface QuoteSummaryBreakdownFulfillment {
  fulfillmentOptions?: FulfillmentDetails;
  country?: SamplingCampaignRequestCountry;
  bvClientId?: string;
}

interface Props {
  /** Pre-formatted total amount string (e.g. "$3,624"). */
  totalAmount?: string;
  content: QuoteSummaryBreakdownContent;
  fulfillment?: QuoteSummaryBreakdownFulfillment;
}

export const QuoteSummaryBreakdown = ({
  content,
  fulfillment,
  totalAmount,
}: Props) => {
  const {
    boxType,
    fulfillmentType,
    packageInsert,
    packageWeight,
    participantCount,
    whoShipsProduct,
  } = fulfillment?.fulfillmentOptions ?? {};

  const isBazaarvoiceShipped =
    whoShipsProduct === WHO_SHIPS_PRODUCT_BAZAARVOICE &&
    fulfillmentType === FULFILLMENT_TYPE_SHIPPED;
  const isClientFulfilled =
    whoShipsProduct === WHO_SHIPS_PRODUCT_MY_COMPANY &&
    fulfillmentType === FULFILLMENT_TYPE_SHIPPED;

  const boxLabel = isDefined(boxType) ? BOX_OPTIONS[boxType].label : undefined;
  const packagingDescription =
    isDefined(participantCount) && isDefined(boxLabel)
      ? `${participantCount} ${boxLabel}`
      : undefined;

  const packageInsertOptions = getPackageInsertOptions(fulfillment?.bvClientId);
  const insertDescription = isDefined(packageInsert)
    ? packageInsertOptions[packageInsert].label
    : undefined;

  const weightDescription = renderPackageWeight(packageWeight);

  const hasReviews = content.writeReview ?? false;
  const hasSocial = content.postOnSocial ?? false;
  const hasPostSurvey = content.postSurvey ?? false;

  const shouldShowDelivery = isBazaarvoiceShipped || isClientFulfilled;

  return (
    <Container>
      {isDefined(totalAmount) && (
        <TotalAmountContainer>
          <CaptionText>Total Amount</CaptionText>
          <TitleText>{totalAmount}</TitleText>
        </TotalAmountContainer>
      )}
      <CardContainer>
        <ContentCard>
          <BodyBoldText>Content</BodyBoldText>

          <DetailRow>
            <DetailLabel>
              <Icon name='product-bag' />
              <CaptionText>Units</CaptionText>
            </DetailLabel>
            <BodyText>{content.units ?? DEFAULT_EMPTY_VALUE}</BodyText>
          </DetailRow>

          <DetailRow>
            <DetailLabel>
              <Icon name='media' />
              <CaptionText>Content</CaptionText>
            </DetailLabel>
            <MultiLineValue>
              {hasReviews && <BodyText>Reviews</BodyText>}
              {hasSocial && <BodyText>Social</BodyText>}
              {hasPostSurvey && <BodyText>Post Survey</BodyText>}
            </MultiLineValue>
          </DetailRow>
        </ContentCard>

        {shouldShowDelivery && <VerticalDivider />}

        {isBazaarvoiceShipped && (
          <SamplingCampaignRequestDeliverySummary
            country={fulfillment?.country}
            insertDescription={insertDescription}
            packagingDescription={packagingDescription}
            weightDescription={weightDescription}
          />
        )}

        {isClientFulfilled && (
          <SamplingCampaignRequestClientFulfillmentSummary />
        )}
      </CardContainer>
    </Container>
  );
};

const Container = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.echo.spacing.sm};
  width: 100%;
  padding: ${theme.echo.spacing.sm};
  background-color: ${theme.colors.gray00};
  border-radius: ${theme.echo.borderRadius.sm};
`,
);

const TotalAmountContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.echo.spacing.xxs};
`,
);

const CardContainer = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: flex-start;
  gap: ${theme.echo.spacing.xs};
  align-self: stretch;
`,
);
